import { useState, useEffect, useRef, useMemo } from 'react';
import moment from 'moment';
import AX from 'axios';
import Select from 'react-select';
import { END_DATE, START_DATE } from '@datepicker-react/hooks';
import axios from '../../_actions/axiosInstance';
import { Page } from '../../components';
import {
  DropdownIndicator,
  IndicatorSeparator,
  SelectStyled,
  themeOverride
} from '../../components/ReactSelectWrapper';
import DoubleRangeDatePicker from '../../components/datepicker/doubleRangePicker/index';
import { NewFullPageSpinner } from '../../helpers/Spiners';
import SearchSelectFacilityDrop from '../../components/SearchSelectFacilityDrop';
import NotifiModal from './NotifiModal';
import { getUserData } from '../../_actions';

const INVENTORY = 'inventory';
const DELIVERY = 'delivery';
const PDI = 'pdi';

const InventoryManagenemtReports = () => {
  const [report, setReport] = useState(INVENTORY);

  const [clientsOpts, setCLientOpts] = useState([]);
  const [siteOpts, setSiteOpts] = useState([]);
  const [classOfTradeOpts, setClassOfTradeOpts] = useState([]);

  const [loading, setLoading] = useState(false);
  const [loadingSites, setLoadingSites] = useState(false);
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingClassOfTrade, setLoadingClassOfTrade] = useState(false);

  const singleClient = useMemo(() => {
    const user = getUserData();
    return user.has_one_inventory_management_report_client;
  }, []);

  const [notify, setNotifi] = useState({});

  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
    focusedInput: START_DATE,
    error: false
  });
  const [inputValue, setInputValue] = useState('');
  const [client, setClient] = useState(null);
  const [sites, setSites] = useState([]);
  const [classOfTrade, setClassOfTrade] = useState([]);

  const sourceClients = useRef({});
  const sourceSites = useRef({});
  const sourceCOT = useRef({});

  const getClientOpts = async () => {
    if (sourceClients?.current?.cancel) {
      sourceSites.current.cancel('Canceled due to new request');
    }
    sourceClients.current = AX.CancelToken.source();
    try {
      setLoadingClients(true);
      const res = await axios.get('/api/v2/inventory_management/clients', {
        cancelToken: sourceClients.current.token
      });
      if (res?.data?.data?.length === 1) {
        setClient(res.data.data[0]);
      }
      setCLientOpts(res.data.data);
    } catch (e) {
      // eslint-disable-next-line
      console.error(e);
    } finally {
      setLoadingClients(false);
    }
  };

  const getSites = async () => {
    if (sourceSites?.current?.cancel) {
      sourceSites.current.cancel('Canceled due to new request');
    }
    sourceSites.current = AX.CancelToken.source();
    if (!client?.id) {
      setSiteOpts([]);
      setSites([]);
      return;
    }
    try {
      setLoadingSites(true);
      const res = await axios.get(`/api/v2/inventory_management/${client.id}/sites`, {
        cancelToken: sourceSites.current.token
      });
      setSiteOpts(res.data.data);
    } catch (e) {
      // eslint-disable-next-line
      console.error(e);
    } finally {
      setSites([]);
      setLoadingSites(false);
    }
  };

  const getClassOfTrade = async () => {
    if (sourceCOT?.current?.cancel) {
      sourceCOT.current.cancel('Canceled due to new request');
    }
    sourceCOT.current = AX.CancelToken.source();
    if (!client?.id) {
      setClassOfTrade([]);
      setClassOfTradeOpts([]);
      return;
    }
    try {
      setLoadingClassOfTrade(true);
      const res = await axios.get(`/api/class_of_trade_options`, {
        cancelToken: sourceCOT.current.token
      });
      setClassOfTradeOpts(res.data.map(cot => ({ id: cot, name: cot })));
    } catch (e) {
      // eslint-disable-next-line
      console.error(e);
    } finally {
      setClassOfTrade([]);
      setLoadingClassOfTrade(false);
    }
  };

  useEffect(() => {
    getClientOpts();
    return () => {
      if (sourceClients?.current?.cancel) {
        sourceClients.current.cancel('Cancelling in cleanup');
      }
    };
  }, []);

  useEffect(() => {
    getSites();
    getClassOfTrade();
    return () => {
      if (sourceSites?.current?.cancel) {
        sourceSites.current.cancel('Cancelling in cleanup');
      }

      if (sourceCOT?.current?.cancel) {
        sourceCOT.current.cancel('Cancelling in cleanup');
      }
    };
  }, [client]);

  const submit = () => {
    setLoading(true);
    const generateParams = {
      client_id: client?.id ? client.id : null,
      date_range: `${moment(dateRange.startDate).format('YYYY-MM-DD')} - ${moment(
        dateRange.endDate
      ).format('YYYY-MM-DD')}`
    };
    if (sites && sites.length) {
      generateParams.site_ids = sites.map(x => x.id);
    }
    if (report === PDI) {
      generateParams.classes_of_trade = classOfTrade.map(x => x.id);
    }
    axios
      .post(`/api/v2/reports/inventory_management_reports/${report}/generate`, generateParams)
      .then(() => {
        if (!singleClient) {
          setClient(null);
        }
        setSites([]);
        setDateRange({
          startDate: null,
          endDate: null,
          focusedInput: START_DATE,
          error: false
        });
        setLoading(false);
        setClassOfTrade([]);
        datepickerRef.current.click();
        setNotifi({
          success: true,
          open: true,
          message:
            'Your request for the report has been successfully submitted. We will notify you via email once the report is ready.'
        });
      })
      .catch(e => {
        let msg = 'Oops';
        if (e.response.data.message) {
          msg = e.response.data.message;
        }
        if (e.response.data.error) {
          msg = e.response.data.error;
        }
        if (e.response.data.errors) {
          msg = e.response.data.errors?.join(' ');
        }
        if (!singleClient) {
          setClient(null);
        }
        setSites([]);
        setDateRange({
          startDate: null,
          endDate: null,
          focusedInput: START_DATE,
          error: false
        });
        setClassOfTrade([]);
        setLoading(false);
        datepickerRef.current.click();
        setNotifi({
          success: false,
          open: true,
          message: msg
        });
      });
  };
  const datepickerRef = useRef();

  const changeReport = r => {
    setReport(r);
  };

  const clearCallback = () => {
    setDateRange({
      startDate: null,
      endDate: null,
      focusedInput: START_DATE,
      error: false
    });
    setInputValue('');
  };
  const formatDate = date => moment(date).format('MM/DD/YYYY');

  const onDateChange = data => {
    let newRange = {};
    let newInput = '';
    if (data?.startDate && data?.endDate) {
      newRange = {
        ...data,
        focusedInput: START_DATE
      };
      newInput = `${formatDate(data.startDate)} - ${formatDate(data.endDate)}`;
    }
    if (data?.startDate && !data?.endDate) {
      newRange = {
        ...data,
        focusedInput: END_DATE
      };
      newInput = `${formatDate(data.startDate)} - `;
    }
    if (data.startDate && data.endDate && data.focusedInput === 'endDate') {
      newRange = {
        ...data,
        endDate: null,
        focusedInput: END_DATE
      };
      newInput = `${formatDate(data.startDate)} - `;
    }
    setDateRange(newRange);
    setInputValue(newInput);

    checkDateRange(newRange);
  };

  const checkDateRange = data => {
    if (!data?.startDate || !data?.endDate) return;
    const duration = moment(data.endDate).diff(moment(data.startDate), 'days');
    const maxDifff = sites.length ? 365 : 31;
    if (duration > maxDifff) {
      const msg = sites.length
        ? 'Selected date range is too long. Please choose a range within one year.'
        : 'Selected date range is too long. Please choose a range within one month.';
      setDateRange({ ...data, error: true, errorMsg: msg });
    } else {
      setDateRange({ ...data, error: false, errorMsg: '' });
    }
  };

  useEffect(() => {
    checkDateRange(dateRange);
  }, [sites]);

  return (
    <Page
      documentName="proteän - Inventory Management Reports"
      pageName="Inventory Management Reports"
      path="Inventory Management Reports"
    >
      <div className="card protean-report fix-picker inventory-management-reports">
        {loading && <NewFullPageSpinner />}
        <div className="card-header">
          <h1 className="report-title">Inventory Management Reports</h1>
        </div>
        <div className="card-body inventory-management-reports-content">
          <div className="col-5 pl-0">
            <div className="button-group">
              <button
                type="button"
                onClick={() => changeReport(INVENTORY)}
                className={`group-btn start ${report === INVENTORY && 'active'}`}
              >
                Inventory Report
              </button>
              <button
                type="button"
                onClick={() => changeReport(DELIVERY)}
                className={`group-btn mid ${report === DELIVERY && 'active'}`}
              >
                Delivery Report
              </button>
              <button
                type="button"
                onClick={() => changeReport(PDI)}
                className={`group-btn end ${report === PDI && 'active'}`}
              >
                PDI Report
              </button>
            </div>

            <div className={`report-input ${singleClient && 'd-none'}`}>
              <div className="report-input-title">Select Client*</div>
              <Select
                theme={themeOverride}
                styles={SelectStyled}
                options={clientsOpts}
                value={client}
                isClearable
                placeholder="Choose client"
                getOptionValue={option => option.id}
                getOptionLabel={option => option.name}
                onChange={val => setClient(val)}
                isLoading={loadingClients}
                components={{ DropdownIndicator, IndicatorSeparator }}
              />
            </div>
            {report !== PDI ? (
              <div className="report-input">
                <div className={`report-input-title ${!client?.id && 'disabled'}`}>Facilities</div>
                <SearchSelectFacilityDrop
                  loading={loadingSites}
                  disabled={!client?.id}
                  onChange={setSites}
                  value={sites}
                  items={siteOpts}
                  placeholder="Search Facilities"
                />
              </div>
            ) : (
              <div className="report-input double-input">
                <div className={`report-input-title ${!client?.id && 'disabled'}`}>Facilities</div>
                <SearchSelectFacilityDrop
                  loading={loadingSites}
                  disabled={!client?.id || !!classOfTrade?.length}
                  onChange={setSites}
                  value={sites}
                  items={siteOpts}
                  placeholder="Search Facilities"
                />
                <div className="orDivider">
                  <div className="topOr">or</div>
                </div>
                <div className={`report-input-title ${!client?.id && 'disabled'}`}>
                  Class of Trade
                </div>
                <SearchSelectFacilityDrop
                  isCOT
                  loading={loadingClassOfTrade}
                  disabled={!client?.id || !!sites?.length}
                  onChange={setClassOfTrade}
                  value={classOfTrade}
                  items={classOfTradeOpts}
                  placeholder="Choose Class of Trade"
                />
              </div>
            )}

            <div className="report-input">
              <div className="report-input-title">Date Range</div>
              <DoubleRangeDatePicker
                value={dateRange}
                onChange={onDateChange}
                inputValue={inputValue}
                clearCallback={clearCallback}
                customRef={datepickerRef}
                oneSide={!sites.length}
              />
              {dateRange?.errorMsg && (
                <span className="dateRange-error">{dateRange?.errorMsg}</span>
              )}
            </div>
            <button
              className="protean-btn protean-btn-default"
              type="button"
              disabled={
                !(dateRange && dateRange.startDate && dateRange.endDate) ||
                !client?.id ||
                dateRange?.error
              }
              onClick={submit}
            >
              Generate Report
            </button>
            <NotifiModal notify={notify} close={() => setNotifi({ ...notify, open: false })} />
          </div>
        </div>
      </div>
    </Page>
  );
};

export default InventoryManagenemtReports;
